import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { Stack } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { GridColDef, GridColumnVisibilityModel, GridFilterModel } from '@mui/x-data-grid-pro';
import { NewButton, PageTitle, RoutedTabs } from 'components';
import { ActivityFilterInput, EntityType, FindActivitiesQuery, SystemFieldId, useFindCustomFieldsQuery, useGetCurrentUserQuery } from 'gql';
import { useFindActivitiesCancellableQuery } from 'gql/cancellableQueries';
import { useCurrentUserRoles } from 'hooks/useCurrentUserRoles';
import { usePersistedPageSize } from 'hooks/usePersistedPageSize';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useCustomFieldsSearchUtils } from '../../../utils/dataGridSearch/customFieldsSearch';
import { ExportToolbar } from '../../../utils/dataGridExport';
import { useExportActivitiesAsExcel } from '../hooks/useExportActivitiesAsExcel';
import { ActivitiesTable } from '../components/ActivitiesTable';
import { ActivityDialog, ActivityFormValues } from '../components/ActivityDialog';
import { useActivityMutations } from '../utils';

const parseVisibilityModelKey = ([key, value]: [string, boolean]) => {
  const [entityType, id] = key.split(';');

  return {
    entityType: entityType as EntityType,
    id,
    visible: value
  };
};

const addSearchParamKey = 'addActivity';
const editSearchParamKey = 'editId';
const activityEntityTypes = [EntityType.Intervention.toString(), EntityType.Distribution.toString(), EntityType.Meeting.toString(), EntityType.Presentation.toString(), EntityType.Routine.toString()];
export type ActivityRow = NonNullable<NonNullable<FindActivitiesQuery['activities']>['items']>[number];

export const ActivitiesPage: React.FC = () => {
  const { formatMessage } = useIntl();

  const [page, setPage] = useState(0);
  const { pageSize, setPageSize } = usePersistedPageSize('activities-tables');
  const [searchTerm, setSearchTerm] = useState('');
  const [gridFilter, setGridFilter] = useState<GridFilterModel>();

  const [activitiesFilter, setActivitiesFilter] = useState<ActivityFilterInput>();

  const [columnIds, setColumnIds] = useState<string[]>();
  const [columns, setColumns] = useState<GridColDef[]>();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>();
  const [includeIndividuals, setIncludeIndividuals] = useState<boolean>();
  const [includeRealities, setIncludeRealities] = useState<boolean>();
  const [includeUnknownGroups, setIncludeUnknownGroups] = useState<boolean>();


  const { data: currentUser, isFetching: isFetchingUser } = useGetCurrentUserQuery();
  const { data: customFields } = useFindCustomFieldsQuery();
  const additionalWorkersFieldId = useMemo(
    () => customFields?.customFields?.find(field => field.systemFieldId === SystemFieldId.AdditionalWorkers)?.id,
    [customFields?.customFields]
  );

  useEffect(() => {
    if (!columnIds || !columnVisibilityModel) return;

    const columnsNotInVisibilityModel = columnIds.filter(id => !Object.hasOwn(columnVisibilityModel, id));

    const entries =
      Object.entries(columnVisibilityModel)
        .concat(columnsNotInVisibilityModel.map(id => [id, true]))
        .map(parseVisibilityModelKey);

    const individualValues = entries.filter(v => v.entityType === EntityType.Individual);
    const unknownGroupValues = entries.filter(v => v.entityType === EntityType.UnknownGroup);
    const realitiesValues = entries.filter(v => v.entityType === EntityType.Reality);

    const hasIndividuals = individualValues.some(v => v.visible);
    const hasUnknownGroups = unknownGroupValues.some(v => v.visible);
    const hasRealities = realitiesValues.some(v => v.visible);

    setIncludeIndividuals(hasIndividuals);
    setIncludeUnknownGroups(hasUnknownGroups);
    setIncludeRealities(hasRealities);



  }, [additionalWorkersFieldId, columnIds, columnVisibilityModel]);
  const { mapGridFilterToCustomValuesFilter } = useCustomFieldsSearchUtils();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('filter');

  useEffect(() => {
    const userTeamIds = currentUser?.me.teams.map(p => p.id);
    let newActivitiesFilter: ActivityFilterInput = { and: [] };

    if (activeTab == null) {
      newActivitiesFilter =
      {
        and: [{
          createdBy: { id: { eq: currentUser?.me.id } }
        }, newActivitiesFilter]
      };
    } else if (activeTab === 'teams') {
      newActivitiesFilter =
      {
        and: [{
          owningTeamId: { in: userTeamIds ?? [] }
        }, newActivitiesFilter]
      };
    }

    if (gridFilter && gridFilter.items.length > 0 && gridFilter.items[0].value != null) {
      const activitiesFilters = gridFilter.items.filter(p => activityEntityTypes.indexOf(p.field.split(';')[0]) !== -1);
      const activityTypeFilters = gridFilter.items.find(p => p.field.split(';')[1] == 'type');
      const individualsFilters = gridFilter.items.filter(p => p.field.split(';')[0] == EntityType.Individual.toString());
      const themeFilter = gridFilter.items.find(p => p.field.split(';')[0] == EntityType.Reality.toString() && p.field.split(';')[1] == 'theme');
      const specificationFilter = gridFilter.items.find(p => p.field.split(';')[0] == EntityType.Reality.toString() && p.field.split(';')[1] == 'specification');
      const referralsFilter = gridFilter.items.find(p => p.field.split(';')[0] == EntityType.Reality.toString() && p.field.split(';')[1] == 'referrals');
      const accompanimentsFilter = gridFilter.items.find(p => p.field.split(';')[0] == EntityType.Reality.toString() && p.field.split(';')[1] == 'accompaniments');
      const approachesFilter = gridFilter.items.find(p => p.field.split(';')[0] == EntityType.Reality.toString() && p.field.split(';')[1] == 'approaches');

      const filters = [newActivitiesFilter];

      if (activitiesFilters.length > 0) {
        // Each filter must be a separate customFieldValues.some clause,
        // otherwise we'd be looking for a single record that matches ALL conditions
        // (which is impossible when filtering on different fieldIds)
        activitiesFilters.forEach(filter => {
          filters.push({ customFieldValues: mapGridFilterToCustomValuesFilter([filter], customFields?.customFields ?? []) });
        });
      }

      if (individualsFilters.length > 0) {
        // Each filter must be a separate clause to avoid the same fieldId matching issue
        individualsFilters.forEach(filter => {
          filters.push({ individuals: { some: { customFieldValues: mapGridFilterToCustomValuesFilter([filter], customFields?.customFields ?? []) } } });
        });
      }

      if (themeFilter) {
        filters.push({ realities: { some: { theme: { i18n: { some: { name: { startsWith: themeFilter.value } } } } } } });
      }

      if (specificationFilter) {
        filters.push({ realities: { some: { specification: { i18n: { some: { name: { startsWith: specificationFilter.value } } } } } } });
      }

      if (referralsFilter) {
        filters.push({
          realities: {
            some: {
              values: {
                some: {
                  and: [
                    { selectionValues: { some: { i18n: { some: { name: { startsWith: referralsFilter.value } } } } } },
                    { field: { systemFieldId: { eq: SystemFieldId.RealityReferrals } } }
                  ]
                }
              }
            }
          }
        });
      }

      if (accompanimentsFilter) {
        filters.push({
          realities: {
            some: {
              values: {
                some: {
                  and: [
                    { selectionValues: { some: { i18n: { some: { name: { startsWith: accompanimentsFilter.value } } } } } },
                    { field: { systemFieldId: { eq: SystemFieldId.RealityAccompaniments } } }
                  ]
                }
              }
            }
          }
        });
      }

      if (approachesFilter) {
        filters.push({
          realities: {
            some: {
              values: {
                some: {
                  and: [
                    { selectionValues: { some: { i18n: { some: { name: { startsWith: approachesFilter.value } } } } } },
                    { field: { systemFieldId: { eq: SystemFieldId.RealityApproaches } } }
                  ]
                }
              }
            }
          }
        });
      }

      if (activityTypeFilters) {
        filters.push({ type: { eq: activityTypeFilters.value } });
      }

      newActivitiesFilter = {
        and: filters
      };
    }

    setActivitiesFilter(newActivitiesFilter);
  }, [activeTab, currentUser?.me.id, currentUser?.me.teams, customFields, gridFilter, mapGridFilterToCustomValuesFilter]);


  const { data: activitiesQuery, isFetching: isFetchingActivities } = useFindActivitiesCancellableQuery({
    pageSize: pageSize,
    skip: page * pageSize,
    filter: activitiesFilter,
    searchTerm: searchTerm !== '' ? searchTerm : undefined,
    includeIndividuals, includeRealities, includeUnknownGroups,
  }, {
    enabled: Boolean(activitiesFilter),
    keepPreviousData: true,
    queryKey: ['findActivities', page, activitiesFilter, searchTerm, includeIndividuals, includeRealities, includeUnknownGroups]
  });
  const activities = useMemo(() => activitiesQuery?.activities?.items ?? [], [activitiesQuery?.activities?.items]);

  const handleDataGridFilter = (filter: GridFilterModel) => {
    setGridFilter(filter?.items.length > 0 ? filter : undefined);
  };

  const handleSearch = useMemo(() => debounce((searchTerm: string) => setSearchTerm(searchTerm), 500), []);

  const addingIntervention = searchParams.get(addSearchParamKey) === 'true';
  const editingInterventionId = Number(searchParams.get(editSearchParamKey)) || undefined;

  const isInterventionDialogOpen = Boolean(editingInterventionId) || addingIntervention;

  const handleClose = () => {
    searchParams.delete(addSearchParamKey);
    searchParams.delete(editSearchParamKey);
    setSearchParams(searchParams);
  };

  const handleAddIntervention = () => {
    searchParams.set(addSearchParamKey, 'true');
    setSearchParams(searchParams);
  };

  const { submitActivity, isSubmitting } = useActivityMutations({
    onSuccess: handleClose
  });

  const onInterventionSubmit = (activityFormValues: ActivityFormValues) => {
    submitActivity(activityFormValues, editingInterventionId);
  };

  const { isAdministrator } = useCurrentUserRoles();

  const tabs = useMemo(() => {
    const tabs = [{
      navigationString: 'mine',
      displayString: formatMessage({ id: 'Mine' })
    }];

    tabs.push({
      navigationString: 'teams',
      displayString: formatMessage({ id: 'My Teams' }),
    });

    if (isAdministrator) {
      tabs.push({
        navigationString: 'all',
        displayString: formatMessage({ id: 'All' }),
      });
    }

    return tabs;
  }, [formatMessage, isAdministrator]);

  const onColumnsChange = (c: GridColDef[]) => {
    setColumns(c);
    setColumnIds(c.map(p => p.field));
  };

  const { handleExport, exporting, exportDisabled, progress } = useExportActivitiesAsExcel({
    columns,
    columnVisibilityModel,
    queryOptions: {
      filter: activitiesFilter,
      searchTerm: searchTerm !== '' ? searchTerm : undefined,
      includeIndividuals,
      includeRealities,
      includeUnknownGroups,
    },
    totalCount: activitiesQuery?.activities?.totalCount ?? 0,
    isFetchingActivities,
    isFetchingUser
  });

  return (
    <Stack mb={{ xs: 9, md: 0 }}>
      <PageTitle icon={<QuestionAnswerIcon />} title={formatMessage({ id: 'Activities' })}
        actionButton={(
          <NewButton onClick={handleAddIntervention} />
        )}
      />

      <RoutedTabs
        thin
        navigationStyle='queryParam'
        queryParam='filter'
        defaultNavigation='mine'
        tabs={tabs}
        sx={{ mb: 1 }}
        variant='scrollable'
        scrollButtons='auto'
      />

      <ActivitiesTable
        onFilter={handleDataGridFilter}
        height={560}
        onSearch={handleSearch}
        loading={isFetchingActivities || isFetchingUser}
        isFetching={isFetchingActivities}
        activities={activities}
        paginationMode='server'
        pagination={true}
        paginationModel={{ page: page, pageSize: pageSize }}
        rowCount={activitiesQuery?.activities?.totalCount ?? 0}
        pageSizeOptions={[10, 25, 50, 100]}
        onPaginationModelChange={useCallback((model: { page: number; pageSize: number }) => {
          setPage(model.page);
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
          }
        }, [pageSize, setPageSize])}
        onEdit={id => {
          searchParams.set(editSearchParamKey, id.toString());
          setSearchParams(searchParams);
        }}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        onColumnsChange={onColumnsChange}
        slots={{ toolbar: ExportToolbar }}
        slotProps={{
          toolbar: {
            onExport: handleExport,
            exporting,
            exportDisabled,
            exportLabel: formatMessage({ id: 'Download as Excel' }),
            progress
          }
        }}
      />

      <ActivityDialog
        isOpen={isInterventionDialogOpen}
        isLoading={isSubmitting}
        onClose={handleClose}
        onSubmit={onInterventionSubmit}
        editingActivityId={editingInterventionId}
      />
    </Stack>
  );
};